import { Show } from './show.model';
import { Log } from './log.model';
import { Showreport } from '../app/showreport/showreport.model';
import { User } from './user.model';

export class ShowPerformance {
    id?: number;
    datetime?: string;
    show?: Show;
    log?: Log = null;
    status?: string = '';
    notes?: string = '';
    location?: string = null;
    showreport?: Showreport = null;
    eftelingShowreport?: string = null;
    documents?: any[] = [];

    scheduledStagemanager?: User = null;
    scheduledToneelmeester?: User = null;
    scheduledShowcaller?: User = null;
    scheduledTroubleshooter?: User = null;
    scheduledFloortechnician?: User = null;
    extraCrew: string | null = null;

    suggestedScheduledToneelmeester?: User = null;

    earlyShiftForStagemanager: boolean = false;
    earlyShiftForShowcaller: boolean = false;
    earlyShiftForTroubleshooter: boolean = false;
    earlyShiftForFloortechnician: boolean = false;
    earlyShiftForToneelmeester: boolean = true;
    earlyShiftForExtraCrew: boolean = false;

    public rehearsalId: number | null = null;
    public rehearsalExternalUrl: string | null = null;
    /**
     * True when the linked rehearsal carries any actual content (notes,
     * purpose, or at least one requirement). False / null when the
     * rehearsal exists only as a shell holding the external URL token.
     * Drives the visibility of the "Repetitie" button on performance
     * cards — an empty rehearsal is not worth opening.
     */
    public rehearsalHasContent: boolean | null = null;

    public createFromJson(json: any): ShowPerformance {
        if (json == null) {
            throw new Error('ShowPerformance.createFromJson: json is null');
        }

        // Instantieer het object
        const instance = new ShowPerformance();

        // Basis attributen
        instance.id = json.id ?? 0;
        instance.datetime = json.datetime || null;
        instance.status = json.status || '';
        instance.notes = json.notes || '';
        instance.location = json.location || null;
        instance.eftelingShowreport = json.eftelingShowreport || null;

        // Sub-objecten
        if (json.show) {

            instance.show = new Show().createFromJson(json.show); // Zorg dat Show een createFromJson methode heeft

        }

        if (json.log) {
            instance.log = new Log().createFromJson(json.log); // Zorg dat Log een createFromJson methode heeft
        } else {
            instance.log = null;
        }

        if (json.showreport) {
            instance.showreport = new Showreport().createFromJson(json.showreport); // Zorg dat Showreport een createFromJson methode heeft
        } else {
            instance.showreport = null;
        }

        if (json.scheduledStagemanager) {
            instance.scheduledStagemanager = new User().createFromJson(json.scheduledStagemanager); // Zorg dat User een createFromJson methode heeft
        }

        if (json.scheduledToneelmeester) {
            instance.scheduledToneelmeester = new User().createFromJson(json.scheduledToneelmeester); // Zorg dat User een createFromJson methode heeft
        }

        if (json.scheduledShowcaller) {
            instance.scheduledShowcaller = new User().createFromJson(json.scheduledShowcaller); // Zorg dat User een createFromJson methode heeft
        }

        if (json.scheduledTroubleshooter) {
            instance.scheduledTroubleshooter = new User().createFromJson(json.scheduledTroubleshooter); // Zorg dat User een createFromJson methode heeft
        }

        if (json.scheduledFloortechnician) {
            instance.scheduledFloortechnician = new User().createFromJson(json.scheduledFloortechnician); // Zorg dat User een createFromJson methode heeft
        }

        instance.extraCrew = json.extraCrew || null;

        // Documenten array
        instance.documents = json.documents ? [...json.documents] : []; // Kopieer de documenten-array als JSON-data aanwezig is.

        instance.earlyShiftForStagemanager = json.earlyShiftForStagemanager ?? false;
        instance.earlyShiftForShowcaller = json.earlyShiftForShowcaller ?? false;
        instance.earlyShiftForTroubleshooter = json.earlyShiftForTroubleshooter ?? false;
        instance.earlyShiftForFloortechnician = json.earlyShiftForFloortechnician ?? false;
        instance.earlyShiftForToneelmeester = json.earlyShiftForToneelmeester ?? false;
        instance.earlyShiftForExtraCrew = json.earlyShiftForExtraCrew ?? false;

        if (json.suggestedScheduledToneelmeester) {
            instance.suggestedScheduledToneelmeester = new User().createFromJson(json.suggestedScheduledToneelmeester);
        }

        instance.rehearsalId = json.rehearsalId ?? null;
        instance.rehearsalExternalUrl = json.rehearsalExternalUrl ?? null;
        instance.rehearsalHasContent = json.rehearsalHasContent ?? null;

        return instance;
    }

    public getWeekNumber(): number {
        const d = new Date(this.datetime);

        d.setDate(d.getDate() + (1 - (d.getDay() || 7)));

        const yearStart = new Date(d.getFullYear(), 0, 1);
        const firstThursday = new Date(yearStart.getTime());
        firstThursday.setDate(1 + (1 - (yearStart.getDay() || 7)));

        return Math.ceil(((d.getTime() - firstThursday.getTime()) / 86400000 + 1) / 7);
    }

    /**
     * True if the performance's datetime is in the past relative to now.
     * Returns false when datetime is missing.
     */
    public isInPast(): boolean {
        if (!this.datetime) { return false; }
        return new Date(this.datetime).getTime() < Date.now();
    }

    /**
     * True when the performance's date (not time) is before today. The day
     * of the show counts as "today" even when the performance time has
     * already passed — use this for UI that should stay visible through the
     * day of the performance. Returns false when datetime is missing.
     */
    public isBeforeToday(): boolean {
        if (!this.datetime) { return false; }
        const performanceDate = new Date(this.datetime);
        const performanceDay = new Date(
            performanceDate.getFullYear(),
            performanceDate.getMonth(),
            performanceDate.getDate()
        );
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return performanceDay.getTime() < today.getTime();
    }

    public hasCallsheet(): boolean {
        const hasRequiredDocument =            this.documents?.some(
                document => document.documentType === 'CALLSHEET' && document.downloadToken != null
            )
        ;
        return hasRequiredDocument || false;
    }

    public getCallsheetUrl(): string | null {
        const document = this.documents?.find(
            doc => doc.documentType === 'CALLSHEET'
        );
        return document?.downloadToken ?? null;
    }

}
